import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import type { IPostsViewPost } from '../../../redux/api/posts/posts-api.types';
import { orderBy } from '../../../utils/collections/array-utils';
import AttachFilesActionButton from '../../designe/ActionButtons/AttachFilesActionButton';
import SendActionButton from '../../designe/ActionButtons/SendActionButton';
import AppFile from '../../designe/app-file/AppFile';
import AppTextField from '../../designe/textfields/app-text-field/AppTextField';
import AppDialog from '../app-dialog/AppDialog';
import ConfirmDialog from '../confirm-dialog/ConfirmDialog';
import PostSkeleton from '../skeletons/post-skeleton/PostSkeleton';
import { useStyles } from './Posts.styles';
import Post from './post/Post';
import ReplyPost from './reply-post/ReplyPost';
import { usePosts } from './usePosts';

interface IPostsProps {
  posts: IPostsViewPost[];
  editable?: boolean;
  newPostBinding?: { communityId: string } | { pageUserId: string };
  allowNewPost?: boolean;
  refreshPosts?: () => void;
  isLoading?: boolean;
}

export default function Posts(props: IPostsProps) {
  const {
    posts,
    editable = true,
    newPostBinding,
    allowNewPost = true,
    refreshPosts,
    isLoading = false,
  } = props;
  const { t } = useTranslation();
  const [deletePostId, setDeletePostId] = useState(null);
  const [repliedPostId, setRepliedPostId] = useState(null);
  const theme = useTheme();

  const { classes } = useStyles(theme);

  const {
    newPost,
    newPostFilesToUpload,
    onPostDelete,
    onSavePost,
    onEditPost,
    onDeleteNewPostFile,
    onUploadNewPostFiles,
    isSavingPost,
    isDeletingPost,
  } = usePosts(newPostBinding, refreshPosts);

  function onReplyPost(post: IPostsViewPost) {
    return onSavePost({ ...post, parentId: repliedPostId });
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSavingPost) {
        onSavePost();
      }
    }
  };
  const normalizedPosts = posts ?? [];
  const postsToRender = normalizedPosts;

  return (
    <div className={classes.userPostsWrapper}>
      {allowNewPost && newPostBinding && (
        <div className={classes.newPostWrapper}>
          {!!newPostFilesToUpload.length && (
            <div className={classes.uploadedFilesWrapper}>
              {newPostFilesToUpload.map((file) => (
                <AppFile
                  key={file.id}
                  file={file}
                  onDelete={(file) => onDeleteNewPostFile(file.id)}
                />
              ))}
            </div>
          )}
          <div className={classes.newPostData}>
            <AppTextField
              placeholder={t('users.newPost')}
              fullWidth
              multiline
              maxRows={5}
              onKeyDown={handleKeyDown}
              value={newPost.content}
              onChange={(e) => onEditPost({ content: e.target.value })}
            />
            <AttachFilesActionButton
              onFileInput={(e) => onUploadNewPostFiles(e.target.files)}
            />
            <SendActionButton
              onClick={() => onSavePost()}
              disabled={!newPost.content && !newPostFilesToUpload.length}
              loading={isSavingPost}
            />
          </div>
        </div>
      )}
      <div className={classes.userPostsContainer}>
        {isLoading && !normalizedPosts.length
          ? Array.from({ length: 3 }).map((_, index) => (
              <PostSkeleton key={`post-skeleton-${index}`} />
            ))
          : postsToRender.map((post) => (
              <Post
                editable={editable}
                post={post}
                key={post.id}
                onPostReplied={setRepliedPostId}
                refreshPosts={refreshPosts}
              />
            ))}
      </div>

      <AppDialog
        open={!!repliedPostId}
        onClose={() => setRepliedPostId(null)}
        title={t('posts.replyPost')}>
        <ReplyPost
          onClose={() => setRepliedPostId(null)}
          onSavePost={onReplyPost}
        />
      </AppDialog>

      <ConfirmDialog
        open={!!deletePostId}
        onClose={() => setDeletePostId(null)}
        title={t('common.delete')}
        isLoading={isDeletingPost}
        onSubmit={() => {
          void onPostDelete(deletePostId).finally(() => setDeletePostId(null));
        }}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </div>
  );
}
